import classes from "./Writeup.module.css"
import { CopyBlock, dracula } from "react-code-blocks"
import { useTranslation } from "react-i18next"

const xortshift_code = `uint32_t advance_seed(uint32_t x32) {
    x32 ^= x32 << 13;
    x32 ^= x32 >> 17;
    x32 ^= x32 << 5;
    return x32;
}`

const calculate_ability = `uint32_t get_branded_ability(uint32_t seed, uint32_t brand) {
    uint32_t ability_roll = seed % max_brand_num(brand);
    return weighted_ability(ability_roll, brand);
}`

const weight_ability = `uint32_t weighted_ability(int ability_roll, uint32_t brand) {
    BrandData brand_data = get_ability_weight(brand);
    uint32_t ability = -1;
    while(ability_roll >= 0) {
        ability++;
        ability_roll -= brand_data[ability].weight;
    }
    return ability;
}`

const drink_ability = `seed = advance_seed(seed);
if(seed % 0x64 <= 0x1D) {
    return drink_ability;
}
seed = advance_seed(seed);
return get_branded_ability_with_drink(seed % max_brand_num_drink(brand, drink), brand, drink);`

const full_code = `uint32_t get_ability(uint32_t seed, uint32_t brand, uint32_t drink) {
    seed = advance_seed(seed);
    uint32_t ret = get_branded_ability(seed, brand);
    if(drink != 0xFFFFFFFF) {
        if(seed % 0x64 <= 0x1D) {
            return drink_ability;
        }
        seed = advance_seed(seed);
        ret = get_branded_ability_with_drink(seed % max_brand_num_drink(brand, drink), brand, drink);
    }
    return ret;
}`

const ability_order = `ability_order = [
    "Ink Saver (Main)",
    "Ink Saver (Sub)",
    "Ink Recovery Up",
    "Run Speed Up",
    "Swim Speed Up",
    "Special Charge Up",
    "Special Saver",
    "Special Power Up",
    "Quick Respawn",
    "Quick Super Jump",
    "Sub Power Up",
    "Ink Resistance Up",
    "Sub Resistance Up",
    "Intensify Action",
]`

const Writeup = props => {
    const { t } = useTranslation()

    return (
        <>
            <h1>Splatoon 3 - {t("misc:Navigation.TechnicalDetails")}</h1>
            <div className={classes.Content}>
                <h2>General Information</h2>
                <p>
                    In Splatoon 3, every time you acquire a new piece of gear (i.e. buying or Salmon Run), this piece of
                    gear gets assigned a randomly generated 32 Bit Integer. Internally it is known as{" "}
                    <code className={classes.ContentCode}>RandomContext</code>. This Number is used to determine the
                    next roll or if the Drink Ticket is working for the next roll. After this, a new seed is calculated.
                </p>
                <h2>XORSHIFT32</h2>
                <p>
                    The algorithm used to advance the seed is <code className={classes.ContentCode}>XORSHIFT32</code>:
                </p>
                <CopyBlock
                    language="c"
                    text={xortshift_code}
                    theme={dracula}
                    wrapLines={true}
                    showLineNumbers={false}
                    codeBlock
                />

                <h2>Rolling Ability</h2>
                <p>
                    At the start of rolling a new ability, the first thing that happens is that the seed is advanced by
                    one, and then a number between 0 (inclusive) and N (exclusive) is generated, with N being 35 for
                    non-neutral Brands and 28 for neutral Brands. After that, the game checks in a table what ability it
                    will roll.
                </p>
                <CopyBlock
                    language="c"
                    text={calculate_ability}
                    theme={dracula}
                    wrapLines={true}
                    showLineNumbers={false}
                    codeBlock
                />
                <h3>Max Brand Num</h3>
                <p>
                    To be a bit more precise about how this number is generated, we take a look at{" "}
                    <a href="https://github.com/Leanny/leanny.github.io/blob/master/splat3/data/parameter/111/misc/spl__BrandTraitsParam.spl__BrandTraitsParam.json">
                        Brand Traits Parameters
                    </a>
                    . For every brand, the <code className={classes.ContentCode}>UnusualGearSkill</code> is worth as
                    defined by much as <code className={classes.ContentCode}>SkillEasilyToGetParam[0]</code>,{" "}
                    <code className={classes.ContentCode}>UsualGearSkill</code> is worth as much as defined by{" "}
                    <code className={classes.ContentCode}>SkillEasilyToGetParam[2]</code>, and every other ability is
                    worth as much as defined by <code className={classes.ContentCode}>SkillEasilyToGetParam[1]</code>.
                </p>
                <h3>Weighted Ability</h3>
                <p>
                    In order to get the correct ability for the ability roll number, the game goes through every ability
                    and subtracts the weight from the ability roll number until it hits 0 or less.
                </p>
                <CopyBlock
                    language="c"
                    text={weight_ability}
                    theme={dracula}
                    wrapLines={true}
                    showLineNumbers={false}
                    codeBlock
                />
                <h2>Drink Tickets</h2>
                <p>
                    Here, the game starts by advancing the seed by one and then checks if seed modulo 100 is smaller or
                    equal 29, so basically a 30% chance for this to happen. If this is the case, then it returns the
                    ability from the drink, otherwise it advances the seed again and calculates a new weighted ability,
                    but this time they skip the ability from the drink effect. This means that non-neutral gear ends up
                    with 25, 33, or 34 as modulo for the seed, and for neutral gear it ends up with 26.
                </p>
                <CopyBlock
                    language="c"
                    text={drink_ability}
                    theme={dracula}
                    wrapLines={true}
                    showLineNumbers={false}
                    codeBlock
                />
                <h2>Full Pseudo-Code</h2>
                <CopyBlock
                    language="c"
                    text={full_code}
                    theme={dracula}
                    wrapLines={true}
                    showLineNumbers={false}
                    codeBlock
                />
                <h2>Internal Ability Order</h2>
                <CopyBlock
                    language="python"
                    text={ability_order}
                    theme={dracula}
                    wrapLines={true}
                    showLineNumbers={false}
                    codeBlock
                />
            </div>
        </>
    )
}

export default Writeup
